import React, { useContext, useEffect, useRef, useState } from "react";
import { Page_settings } from "../../config/page_settings";
import { IClassList } from "../../type/classroom";
import colors from "../../design/colors";
import styled from "styled-components";
import { ChevronDown, ChevronUp } from "../icons/chevron";
import { rootStore } from "../../mobx/store";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import BasicDropdown from "./dropdown/Basic_dropdown";
import ExitIcon from "../icons/exit";
import { useUser } from "@/hooks/queries/0.useAuth";

/* **********************************
 * COMPONENT
 * 컴포넌트 정의
 ********************************** */
const Header = () => {
  /* **********************************
   * GLOBAL STATE
   * 전역 상태 및 전역 변수
   ********************************** */
  const context: any = useContext(Page_settings);
  const isCorpMode = rootStore.getCorpMode;
  const userType = rootStore.getProfile?.userType;
  const token = rootStore.token;
  const name = rootStore.getProfile?.name;
  const userRole = rootStore.getProfile?.role;
  const me = rootStore.getMe;
  const navigate = useNavigate();

  // permissions를 매번 실시간으로 확인
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined);

  /* **********************************
   * HOOKS
   ********************************** */
  const { data: userInfo } = useUser();

  /* **********************************
   * LOCAL STATE
   * 컴포넌트 내부에서 사용하는 상태
   ********************************** */
  const selectedClass = useRef<IClassList | null>(null);
  const [isOpenClassDropdown, setIsOpenClassDropdown] =
    useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<
    "chatBot" | "talk" | "notice" | "setting" | null
  >(null);

  const classDropdownRef = useRef<HTMLDivElement>(null);

  /* **********************************
   * USE EFFECT
   * 컴포넌트 렌더링 시 실행되는 함수
   ********************************** */
  useEffect(() => {
    if (userInfo?.permissions) {
      setUserPermissions(userInfo.permissions);
      setUserEmail(userInfo.email);
    }
  }, [userInfo]);

  // useEffect(() => {
  //   if (!token) return;

  //   // 웹소켓 클라이언트 생성 및 설정
  //   const client = new Client({
  //     webSocketFactory: () => new SockJS(`${SOCKET_URL}/ws`),
  //     connectHeaders: {
  //       "x-access-token": token,
  //     },
  //     reconnectDelay: 3000, // 연결 끊김 후 재연결 대기 시간(ms)
  //     onConnect: () => {
  //       console.log(">> [WebSocket] Connected <<");
  //     },
  //     onDisconnect: () => {
  //       console.log(">> [WebSocket] Disconnected <<");
  //     },
  //     onStompError: (frame) => {
  //       console.error("[WebSocket] STOMP Error:", frame);
  //     },
  //     onWebSocketClose: (evt) => {
  //       console.warn("[WebSocket] Closed:", evt);
  //     },
  //   });

  //   client.activate();
  //   setSocketClient(client);

  //   return () => {
  //     if (client && client.active) {
  //       client.deactivate();
  //     }
  //   };
  // }, [token]);

  /* **********************************
   * HANDLERS
   * 사용자 입력 및 이벤트 처리
   ********************************** */
  // 클래스 드롭다운 외부 클릭 이벤트
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        classDropdownRef.current &&
        !classDropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpenClassDropdown(false);
      }
    };

    if (isOpenClassDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpenClassDropdown]);

  return (
    <HeaderWrapper>
      <section style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <BasicDropdown
          customTrigger={(open) => (
            <>
              <span>
                관리자{" "}
                <span style={{ color: colors.gray400 }}>
                  {userEmail?.split("@")[0] || ""}
                </span>{" "}
                님
              </span>
              {open ? (
                <ChevronUp width="20px" height="20px" color={colors.gray900} />
              ) : (
                <ChevronDown
                  width="20px"
                  height="20px"
                  color={colors.gray900}
                />
              )}
            </>
          )}
          options={[
            {
              label: "로그아웃",
              icon: <ExitIcon />,
              onClick: () => {
                rootStore.signOut();
                navigate("/login");
              },
            },
          ]}
        />
      </section>
    </HeaderWrapper>
  );
};

const HeaderWrapper = styled.div`
  width: 100%;
  height: 80px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: end;
  padding: 0 60px;
  padding-top: env(safe-area-inset-top, 0px);
  background-color: ${colors.white};
  z-index: 5;
  position: relative;
  border-bottom: 1px solid ${colors.gray100};

  ${({ theme }) => theme.device.mobile} {
    padding: 0 20px;
    padding-top: env(safe-area-inset-top, 0px);
    height: calc(50px + env(safe-area-inset-top, 0px));
  }
`;

const ClassSection = styled.div<{ $isOpen: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 250px;
  height: 56px;
  gap: 10px 8px;
  background-color: ${(props) =>
    props.$isOpen ? colors.gray50 : colors.white};
  padding: 6px 8px;
  border-radius: 8px;
`;

const SettingBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: ${colors.white};
  border-radius: 8px;
  padding: 0 8px;
  width: 144px;
  height: 44px;
  margin-left: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const ClassDropdown = styled.div`
  position: absolute;
  top: 80px;
  left: 20px;
  width: 250px;
  max-height: 440px;
  background-color: ${colors.white};
  border-radius: 8px;
  padding: 8px 10px 16px 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px; /* Width of the scrollbar */
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${colors.gray100}; /* Color of the scrollbar thumb */
    border-radius: 4px; /* Rounded corners for the scrollbar thumb */
  }
  &::-webkit-scrollbar-track {
    background: transparent; /* Background of the scrollbar track */
  }
`;

const ClassDropdownItem = styled.div<{ isHoverd: boolean }>`
  width: 100%;
  height: 56px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  border-radius: 8px;
  background-color: ${(props) =>
    props.isHoverd ? colors.gray50 : colors.white};
  padding: 8px 10px;
`;

export default observer(Header);
